import { c as _c } from "react/compiler-runtime";
import type { InterfaceToken, MarketForRewards, Rewards } from '@morpho-org/dapp-core';
import { safeParseNumber } from '@morpho-org/blue-sdk-viem';
import { ZERO } from '@morpho-org/dapp-core';
import { isDefined } from '@morpho-org/morpho-ts';
import { formatters, getDigitsFromPriceAndAmount } from '@morpho-org/ui-v6/utils';
import { useMemo } from 'react';
import { calculateProjectedEarningsMonthly, calculateProjectedEarningsYearly } from '@/helpers/apy';
import { getParsedValue } from '@/helpers/formatters';
import { getNetRateOnMarket } from '@/helpers/getNativeApy';
import { getMarketRewards } from '@/helpers/rewards';
interface UseMarketLendSummaryDataProps {
  /** Current on-chain loan-asset supply (`AccrualPosition.supplyAssets`). */
  initialSupply: bigint | undefined;
  /** Supply after the pending lend (undefined ⇒ no "after" value shown). */
  finalSupply: bigint | undefined;
  loanAsset: InterfaceToken;
  avgSupplyApy?: number | null;
  marketForRewards?: MarketForRewards | null;
}
interface SupplyPositionData {
  initial: string;
  initialDetailed?: string;
  final?: string;
  finalDetailed?: string;
  showFinalValue: boolean;
}
interface SupplyApyData {
  apy?: bigint | null;
  netApy?: bigint | null;
  formattedNetApy: string;
  rewards: Rewards[];
}
interface ProjectedEarningsSide {
  initial: string;
  initialTooltip?: string;
  final?: string;
  finalTooltip?: string;
  showFinalValue: boolean;
}
interface ProjectedEarningsData {
  monthly: ProjectedEarningsSide;
  yearly: ProjectedEarningsSide;
}

/** Vault-convention USD formatting for a projected-earnings before-value (defaults to $0.00). */
const formatProjectedEarningsInitial = (valueUsd: bigint | undefined) => ({
  formatted: formatters.short.digits(2).default('$0.00').unit('$').max(100_000_000).of(valueUsd, 18),
  detailed: formatters.commas.digits(2).unit('$').default('$0.00').max(100_000_000).of(valueUsd, 18)
});

/** Vault-convention USD formatting for a projected-earnings after-value (undefined until it resolves). */
const formatProjectedEarningsFinal = (valueUsd: bigint | undefined) => ({
  formatted: formatters.short.digits(2).unit('$').max(100_000_000).of(valueUsd, 18),
  detailed: formatters.commas.digits(2).unit('$').default('$0.00').max(100_000_000).of(valueUsd, 18)
});

/**
 * Formatting for the lend (loan-asset supply) surfaces — the supply-side sister of
 * `useMarketSummaryData`, shared by the panel summary card and the lend recap so the
 * two stay consistent. Net APY = native supply APY + supply-side rewards
 * (`supplyApr`, positive — they add to the lender's earnings, unlike borrow rewards
 * which are negated).
 */
export const useMarketLendSummaryData = t0 => {
  const $ = _c(83);
  const {
    initialSupply,
    finalSupply,
    loanAsset,
    avgSupplyApy,
    marketForRewards
  } = t0;
  let t1;
  if ($[0] !== marketForRewards) {
    t1 = getMarketRewards(marketForRewards, "supply");
    $[0] = marketForRewards;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const rewards = t1;
  let t2;
  if ($[2] !== avgSupplyApy) {
    t2 = getParsedValue(avgSupplyApy, safeParseNumber);
    $[2] = avgSupplyApy;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const apy = t2;
  let t3;
  if ($[4] !== apy || $[5] !== rewards) {
    t3 = getNetRateOnMarket({
      rewards,
      nativeRate: apy
    });
    $[4] = apy;
    $[5] = rewards;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const netApy = t3;
  let t4;
  if ($[7] !== netApy) {
    t4 = formatters.percent.unit("%").digits(2).default("-").max(10).of(netApy, 18);
    $[7] = netApy;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const formattedNetApy = t4;
  let t5;
  if ($[9] !== initialSupply || $[10] !== loanAsset.decimals || $[11] !== loanAsset.price) {
    t5 = formatters.short.digits(getDigitsFromPriceAndAmount(loanAsset.price, initialSupply, loanAsset.decimals)).trailingZero(!initialSupply).default("-").of(initialSupply, loanAsset.decimals);
    $[9] = initialSupply;
    $[10] = loanAsset.decimals;
    $[11] = loanAsset.price;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const formattedInitial = t5;
  let t6;
  if ($[13] !== initialSupply || $[14] !== loanAsset.decimals || $[15] !== loanAsset.price || $[16] !== loanAsset.symbol) {
    t6 = initialSupply && initialSupply > ZERO ? formatters.commas.digits(getDigitsFromPriceAndAmount(loanAsset.price, initialSupply, loanAsset.decimals)).trailingZero(false).unit(loanAsset.symbol).of(initialSupply, loanAsset.decimals) : undefined;
    $[13] = initialSupply;
    $[14] = loanAsset.decimals;
    $[15] = loanAsset.price;
    $[16] = loanAsset.symbol;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  const formattedInitialDetailed = t6;
  let t7;
  if ($[18] !== finalSupply || $[19] !== loanAsset.decimals || $[20] !== loanAsset.price) {
    t7 = formatters.short.digits(getDigitsFromPriceAndAmount(loanAsset.price, finalSupply, loanAsset.decimals)).trailingZero(!finalSupply).default("-").of(finalSupply, loanAsset.decimals);
    $[18] = finalSupply;
    $[19] = loanAsset.decimals;
    $[20] = loanAsset.price;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  const formattedFinal = t7;
  let t8;
  if ($[22] !== finalSupply || $[23] !== loanAsset.decimals || $[24] !== loanAsset.price || $[25] !== loanAsset.symbol) {
    t8 = finalSupply && finalSupply > ZERO ? formatters.commas.digits(getDigitsFromPriceAndAmount(loanAsset.price, finalSupply, loanAsset.decimals)).trailingZero(false).unit(loanAsset.symbol).of(finalSupply, loanAsset.decimals) : undefined;
    $[22] = finalSupply;
    $[23] = loanAsset.decimals;
    $[24] = loanAsset.price;
    $[25] = loanAsset.symbol;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  const formattedFinalDetailed = t8;
  let t9;
  if ($[27] !== finalSupply || $[28] !== initialSupply) {
    t9 = isDefined(finalSupply) && finalSupply !== initialSupply;
    $[27] = finalSupply;
    $[28] = initialSupply;
    $[29] = t9;
  } else {
    t9 = $[29];
  }
  let t10;
  if ($[30] !== formattedFinal || $[31] !== formattedFinalDetailed || $[32] !== formattedInitial || $[33] !== formattedInitialDetailed || $[34] !== t9) {
    t10 = {
      initial: formattedInitial,
      final: formattedFinal,
      initialDetailed: formattedInitialDetailed,
      finalDetailed: formattedFinalDetailed,
      showFinalValue: t9
    };
    $[30] = formattedFinal;
    $[31] = formattedFinalDetailed;
    $[32] = formattedInitial;
    $[33] = formattedInitialDetailed;
    $[34] = t9;
    $[35] = t10;
  } else {
    t10 = $[35];
  }
  const supply = t10;
  let t11;
  if ($[36] !== finalSupply || $[37] !== initialSupply) {
    t11 = isDefined(finalSupply) && finalSupply !== initialSupply;
    $[36] = finalSupply;
    $[37] = initialSupply;
    $[38] = t11;
  } else {
    t11 = $[38];
  }
  const showFinalValue = t11;
  let t12;
  if ($[39] !== initialSupply || $[40] !== loanAsset || $[41] !== netApy) {
    t12 = formatProjectedEarningsInitial(calculateProjectedEarningsMonthly(netApy, initialSupply, loanAsset));
    $[39] = initialSupply;
    $[40] = loanAsset;
    $[41] = netApy;
    $[42] = t12;
  } else {
    t12 = $[42];
  }
  const monthlyInitial = t12;
  let t13;
  let t14;
  let t15;
  let t16;
  let t17;
  let t18;
  if ($[43] !== finalSupply || $[44] !== initialSupply || $[45] !== loanAsset || $[46] !== monthlyInitial.detailed || $[47] !== monthlyInitial.formatted || $[48] !== netApy || $[49] !== showFinalValue) {
    const monthlyFinal = formatProjectedEarningsFinal(calculateProjectedEarningsMonthly(netApy, finalSupply, loanAsset));
    let t19;
    if ($[56] !== initialSupply || $[57] !== loanAsset || $[58] !== netApy) {
      t19 = formatProjectedEarningsInitial(calculateProjectedEarningsYearly(netApy, initialSupply, loanAsset));
      $[56] = initialSupply;
      $[57] = loanAsset;
      $[58] = netApy;
      $[59] = t19;
    } else {
      t19 = $[59];
    }
    const yearlyInitial = t19;
    const yearlyFinal = formatProjectedEarningsFinal(calculateProjectedEarningsYearly(netApy, finalSupply, loanAsset));
    const t20 = showFinalValue && isDefined(monthlyFinal.formatted);
    if ($[60] !== monthlyFinal || $[61] !== monthlyInitial.detailed || $[62] !== monthlyInitial.formatted || $[63] !== t20) {
      t18 = {
        initial: monthlyInitial.formatted,
        initialTooltip: monthlyInitial.detailed,
        final: monthlyFinal.formatted,
        finalTooltip: monthlyFinal.detailed,
        showFinalValue: t20
      };
      $[60] = monthlyFinal;
      $[61] = monthlyInitial.detailed;
      $[62] = monthlyInitial.formatted;
      $[63] = t20;
      $[64] = t18;
    } else {
      t18 = $[64];
    }
    t13 = yearlyInitial.formatted;
    t14 = yearlyInitial.detailed;
    t15 = yearlyFinal.formatted;
    t16 = yearlyFinal.detailed;
    t17 = showFinalValue && isDefined(yearlyFinal.formatted);
    $[43] = finalSupply;
    $[44] = initialSupply;
    $[45] = loanAsset;
    $[46] = monthlyInitial.detailed;
    $[47] = monthlyInitial.formatted;
    $[48] = netApy;
    $[49] = showFinalValue;
    $[50] = t13;
    $[51] = t14;
    $[52] = t15;
    $[53] = t16;
    $[54] = t17;
    $[55] = t18;
  } else {
    t13 = $[50];
    t14 = $[51];
    t15 = $[52];
    t16 = $[53];
    t17 = $[54];
    t18 = $[55];
  }
  let t19;
  if ($[65] !== t13 || $[66] !== t14 || $[67] !== t15 || $[68] !== t16 || $[69] !== t17) {
    t19 = {
      initial: t13,
      initialTooltip: t14,
      final: t15,
      finalTooltip: t16,
      showFinalValue: t17
    };
    $[65] = t13;
    $[66] = t14;
    $[67] = t15;
    $[68] = t16;
    $[69] = t17;
    $[70] = t19;
  } else {
    t19 = $[70];
  }
  let t20;
  if ($[71] !== t18 || $[72] !== t19) {
    t20 = {
      monthly: t18,
      yearly: t19
    };
    $[71] = t18;
    $[72] = t19;
    $[73] = t20;
  } else {
    t20 = $[73];
  }
  const projectedEarnings = t20;
  let t21;
  if ($[74] !== apy || $[75] !== formattedNetApy || $[76] !== netApy || $[77] !== rewards) {
    t21 = {
      apy,
      netApy,
      formattedNetApy,
      rewards
    };
    $[74] = apy;
    $[75] = formattedNetApy;
    $[76] = netApy;
    $[77] = rewards;
    $[78] = t21;
  } else {
    t21 = $[78];
  }
  let t22;
  if ($[79] !== projectedEarnings || $[80] !== supply || $[81] !== t21) {
    t22 = {
      supply,
      apy: t21,
      projectedEarnings
    };
    $[79] = projectedEarnings;
    $[80] = supply;
    $[81] = t21;
    $[82] = t22;
  } else {
    t22 = $[82];
  }
  return t22;
};